import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Stack,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Download as ExportIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { ChangeTrackingService } from '../../services/changeTrackingService';
import {
  Change,
  ChangeStatus,
  ChangeEntityType,
  ChangeType,
  ContributionSource,
} from '../../types';
import { ChangeCard } from '../../components/changes/ChangeCard';
import { DiffViewer } from '../../components/changes/DiffViewer';
import { useAuth } from '../../contexts/AuthContext';

/**
 * AllChanges - Master changes page showing all changes across the system
 *
 * Features (Phase 4.3):
 * - View all changes in the system
 * - Filter by source (user, API, import, system, merge, admin)
 * - Filter by entity type, change type, status, user
 * - Filter by date range
 * - Export to CSV
 * - Sortable columns
 * - Statistics dashboard
 */

export const AllChanges: React.FC = () => {
  const { authState } = useAuth();
  const user = authState.user;

  // ===== State =====
  const [changes, setChanges] = useState<Change[]>([]);
  const [filteredChanges, setFilteredChanges] = useState<Change[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedChange, setSelectedChange] = useState<Change | null>(null);
  const [diffViewerOpen, setDiffViewerOpen] = useState(false);

  // Filter state
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [entityTypeFilter, setEntityTypeFilter] = useState<string>('all');
  const [changeTypeFilter, setChangeTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [userFilter, setUserFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');

  // Statistics
  const [stats, setStats] = useState({
    total: 0,
    bySource: {} as Record<ContributionSource, number>,
    byStatus: {} as Record<ChangeStatus, number>,
    byEntityType: {} as Record<ChangeEntityType, number>,
  });

  // ===== Effects =====

  useEffect(() => {
    loadChanges();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [
    changes,
    sourceFilter,
    entityTypeFilter,
    changeTypeFilter,
    statusFilter,
    userFilter,
    searchQuery,
  ]);

  useEffect(() => {
    calculateStats();
  }, [changes]);

  // ===== Data Loading =====

  const loadChanges = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load all changes
      const allChanges = await ChangeTrackingService.getAllChanges(undefined, 1000);
      setChanges(allChanges);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = () => {
    const sourceStats = ChangeTrackingService.getChangeStatisticsBySource(changes);

    const statusStats: Record<string, number> = {};
    const entityTypeStats: Record<string, number> = {};

    changes.forEach(change => {
      // Count by status
      statusStats[change.status] = (statusStats[change.status] || 0) + 1;

      // Count by entity type
      entityTypeStats[change.entityType] = (entityTypeStats[change.entityType] || 0) + 1;
    });

    setStats({
      total: changes.length,
      bySource: sourceStats,
      byStatus: statusStats as Record<ChangeStatus, number>,
      byEntityType: entityTypeStats as Record<ChangeEntityType, number>,
    });
  };

  const applyFilters = () => {
    let filtered = [...changes];

    // Filter by source
    if (sourceFilter !== 'all') {
      filtered = filtered.filter(c => c.source === sourceFilter);
    }

    // Filter by entity type
    if (entityTypeFilter !== 'all') {
      filtered = filtered.filter(c => c.entityType === entityTypeFilter);
    }

    // Filter by change type
    if (changeTypeFilter !== 'all') {
      filtered = filtered.filter(c => c.changeType === changeTypeFilter);
    }

    // Filter by status
    if (statusFilter !== 'all') {
      if (statusFilter === 'approved') {
        filtered = filtered.filter(c => c.status === 'approved' || c.status === 'auto-approved');
      } else {
        filtered = filtered.filter(c => c.status === statusFilter);
      }
    }

    // Filter by user
    if (userFilter) {
      const userQuery = userFilter.toLowerCase();
      filtered = filtered.filter(c =>
        c.userName.toLowerCase().includes(userQuery) ||
        c.userId.toLowerCase().includes(userQuery)
      );
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(c =>
        c.userName.toLowerCase().includes(query) ||
        c.entityId.toLowerCase().includes(query) ||
        (c.reason && c.reason.toLowerCase().includes(query)) ||
        c.entityType.toLowerCase().includes(query)
      );
    }

    setFilteredChanges(filtered);
  };

  // ===== Event Handlers =====

  const handleApprove = async (changeId: string) => {
    if (!user) return;

    try {
      setError(null);
      setSuccess(null);

      await ChangeTrackingService.approveChange(
        changeId,
        user.uid,
        user.displayName || 'Admin',
        'Approved from all changes page'
      );

      setSuccess('Change approved successfully');
      await loadChanges();
    } catch (err: any) {
      setError(`Failed to approve change: ${err.message}`);
    }
  };

  const handleReject = async (changeId: string, reason?: string) => {
    if (!user) return;

    const rejectionReason = reason || prompt('Please provide a reason for rejection:');
    if (!rejectionReason) return;

    try {
      setError(null);
      setSuccess(null);

      await ChangeTrackingService.rejectChange(
        changeId,
        user.uid,
        user.displayName || 'Admin',
        rejectionReason
      );

      setSuccess('Change rejected successfully');
      await loadChanges();
    } catch (err: any) {
      setError(`Failed to reject change: ${err.message}`);
    }
  };

  const handleViewDetails = (change: Change) => {
    setSelectedChange(change);
    setDiffViewerOpen(true);
  };

  const handleRefresh = () => {
    loadChanges();
  };

  const handleExportCSV = () => {
    try {
      // Create CSV content
      const headers = [
        'Date',
        'User',
        'Entity Type',
        'Entity ID',
        'Change Type',
        'Source',
        'Status',
        'Reason',
      ];

      const rows = filteredChanges.map(change => [
        new Date(change.createdAt).toISOString(),
        change.userName,
        change.entityType,
        change.entityId,
        change.changeType,
        change.source || 'user',
        change.status,
        change.reason || '',
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
      ].join('\n');

      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `changes_export_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);

      setSuccess('Changes exported to CSV successfully');
    } catch (err: any) {
      setError(`Failed to export CSV: ${err.message}`);
    }
  };

  const handleClearFilters = () => {
    setSourceFilter('all');
    setEntityTypeFilter('all');
    setChangeTypeFilter('all');
    setStatusFilter('all');
    setUserFilter('');
    setSearchQuery('');
  };

  // ===== Render =====

  const activeFiltersCount = [
    sourceFilter !== 'all',
    entityTypeFilter !== 'all',
    changeTypeFilter !== 'all',
    statusFilter !== 'all',
    userFilter !== '',
    searchQuery !== '',
  ].filter(Boolean).length;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Page Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            All Changes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Master view of all changes across the system
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={handleExportCSV}
            disabled={loading || filteredChanges.length === 0}
          >
            Export CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4">{stats.total}</Typography>
              <Typography variant="body2" color="text.secondary">
                Total Changes
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4">
                {stats.bySource?.user || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                User Edits
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4">
                {(stats.bySource?.['api-ancestry'] || 0) +
                  (stats.bySource?.['api-familysearch'] || 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                API Changes
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4">
                {(stats.bySource?.['import-gedcom'] || 0) +
                  (stats.bySource?.['import-csv'] || 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Imports
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Main Content */}
      <Paper sx={{ p: 3 }}>
        {/* Filters */}
        <Box mb={3}>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <FilterIcon />
            <Typography variant="h6">Filters</Typography>
            {activeFiltersCount > 0 && (
              <>
                <Chip label={`${activeFiltersCount} active`} size="small" color="primary" />
                <Button size="small" onClick={handleClearFilters}>
                  Clear All
                </Button>
              </>
            )}
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Search"
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by user, entity, or reason..."
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Source</InputLabel>
                <Select
                  value={sourceFilter}
                  onChange={(e) => setSourceFilter(e.target.value)}
                  label="Source"
                >
                  <MenuItem value="all">All Sources</MenuItem>
                  <MenuItem value="user">User Edits</MenuItem>
                  <MenuItem value="api-ancestry">Ancestry API</MenuItem>
                  <MenuItem value="api-familysearch">FamilySearch API</MenuItem>
                  <MenuItem value="import-gedcom">GEDCOM Import</MenuItem>
                  <MenuItem value="import-csv">CSV Import</MenuItem>
                  <MenuItem value="system">System</MenuItem>
                  <MenuItem value="merge">Merge</MenuItem>
                  <MenuItem value="admin-correction">Admin Correction</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Entity Type</InputLabel>
                <Select
                  value={entityTypeFilter}
                  onChange={(e) => setEntityTypeFilter(e.target.value)}
                  label="Entity Type"
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="person">Person</MenuItem>
                  <MenuItem value="relationship">Relationship</MenuItem>
                  <MenuItem value="photo">Photo</MenuItem>
                  <MenuItem value="document">Document</MenuItem>
                  <MenuItem value="memory">Memory</MenuItem>
                  <MenuItem value="note">Note</MenuItem>
                  <MenuItem value="comment">Comment</MenuItem>
                  <MenuItem value="research">Research Note</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Change Type</InputLabel>
                <Select
                  value={changeTypeFilter}
                  onChange={(e) => setChangeTypeFilter(e.target.value)}
                  label="Change Type"
                >
                  <MenuItem value="all">All Changes</MenuItem>
                  <MenuItem value="create">Create</MenuItem>
                  <MenuItem value="update">Update</MenuItem>
                  <MenuItem value="delete">Delete</MenuItem>
                  <MenuItem value="merge">Merge</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        {/* Results Summary */}
        <Box mb={2}>
          <Typography variant="body2" color="text.secondary">
            Showing {filteredChanges.length} of {stats.total} changes
          </Typography>
        </Box>

        {/* Changes List */}
        {loading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        ) : filteredChanges.length === 0 ? (
          <Box textAlign="center" py={8}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No changes found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {activeFiltersCount > 0
                ? 'Try adjusting your filters'
                : 'No changes have been made yet'}
            </Typography>
          </Box>
        ) : (
          <Box>
            {filteredChanges.map((change) => (
              <ChangeCard
                key={change.id}
                change={change}
                onApprove={handleApprove}
                onReject={handleReject}
                onViewDetails={handleViewDetails}
                showActions={change.status === 'pending'}
              />
            ))}
          </Box>
        )}
      </Paper>

      {/* Diff Viewer Dialog */}
      {selectedChange && (
        <DiffViewer
          change={selectedChange}
          open={diffViewerOpen}
          onClose={() => setDiffViewerOpen(false)}
          onApprove={selectedChange.status === 'pending' ? handleApprove : undefined}
          onReject={
            selectedChange.status === 'pending'
              ? (id, reason) => handleReject(id, reason)
              : undefined
          }
          showActions={selectedChange.status === 'pending'}
        />
      )}
    </Container>
  );
};
